import {
  LegacyImportError,
  ProjectDocument,
  PROJECT_EXTENSION,
  deserialiseProject,
  detectFormat,
  importLegacyProject,
  importLegacyWall,
  serialiseProject,
} from '@bw/document';
import type { LibraryEntry, Wall, WallPlacement } from '@bw/document';
import {
  BrowserStorage,
  openProjectFile,
  openWallFile,
  writeProjectFile,
} from '../storage/browser-storage.js';

export const storage = new BrowserStorage();

export interface LoadOutcome {
  readonly project: ProjectDocument;
  readonly droppedBricks: number;
}

export interface WallLoadOutcome {
  readonly wall: Wall;
  readonly droppedBricks: number;
}

export class RepairNeeded extends Error {
  override readonly name = 'RepairNeeded';
  constructor(
    readonly detail: LegacyImportError,
    readonly text: string,
    readonly fileName: string,
    /** Which action was refused, so retrying can repeat the right one. */
    readonly target: 'project' | 'wall' = 'project',
  ) {
    super(detail.message);
  }
}

/** A legacy file names the wall it becomes, minus whichever suffix it wore. */
export function wallNameFor(fileName: string): string {
  return (
    fileName.replace(/\.brickwall\.json$/i, '').replace(/\.json$/i, '') || 'Imported wall'
  );
}

/** Parse whichever format a file turns out to be (design D2, D3). */
export function loadProjectText(text: string, fileName: string, repair = false): LoadOutcome {
  const format = detectFormat(text);
  if (format === 'brickwall-project') {
    return { project: deserialiseProject(text), droppedBricks: 0 };
  }
  const name = wallNameFor(fileName);
  try {
    const result = importLegacyProject(text, { name, repair });
    return { project: result.project, droppedBricks: result.dropped.length };
  } catch (error) {
    if (error instanceof LegacyImportError && error.repairable && !repair) {
      throw new RepairNeeded(error, text, fileName);
    }
    throw error;
  }
}

/**
 * Import one legacy wall into the project that is already open (design D3).
 *
 * A `.bwall` is turned away rather than merged: it is a whole project with its
 * own walls, materials and roof, and quietly taking one wall out of it — or
 * all of them — is not what either reading of "import a wall" means.
 */
export function importWallText(
  project: ProjectDocument,
  text: string,
  fileName: string,
  placement: WallPlacement,
  repair = false,
): WallLoadOutcome {
  if (detectFormat(text) === 'brickwall-project') {
    throw new Error(
      'That is a Brickwall project, not a single wall. Open it with Ctrl+O to get all of its walls.',
    );
  }
  try {
    const result = importLegacyWall(project, text, {
      name: wallNameFor(fileName),
      placement,
      repair,
    });
    return { wall: result.wall, droppedBricks: result.dropped.length };
  } catch (error) {
    if (error instanceof LegacyImportError && error.repairable && !repair) {
      throw new RepairNeeded(error, text, fileName, 'wall');
    }
    throw error;
  }
}

export async function pickAndLoad(): Promise<
  { outcome: LoadOutcome; name: string; handle: FileSystemFileHandle | null } | null
> {
  const opened = await openProjectFile();
  if (opened === null) return null;
  return {
    outcome: loadProjectText(opened.text, opened.name),
    name: opened.name,
    handle: opened.handle,
  };
}

/** Choose a legacy wall file. The caller imports it, so it can undo it too. */
export async function pickWall(): Promise<{ text: string; name: string } | null> {
  const opened = await openWallFile();
  return opened === null ? null : { text: opened.text, name: opened.name };
}

export function suggestedFileName(project: ProjectDocument): string {
  const base = project.name.trim() === '' ? 'Untitled' : project.name.trim();
  return base.endsWith(PROJECT_EXTENSION) ? base : `${base}${PROJECT_EXTENSION}`;
}

export async function saveProject(
  project: ProjectDocument,
  handle: FileSystemFileHandle | null,
): Promise<{ handle: FileSystemFileHandle | null; name: string } | null> {
  // Serialise fully in memory, then write once (design D2).
  const text = serialiseProject(project);
  const result = await writeProjectFile(handle, suggestedFileName(project), text);
  if (result !== null) {
    await storage.putLibrary(libraryEntryFor(project), text);
  }
  return result;
}

export function libraryEntryFor(project: ProjectDocument): LibraryEntry {
  return {
    id: `${project.meta.created}|${project.name}`,
    name: project.name,
    savedAt: new Date().toISOString(),
    brickCount: project.brickCount,
  };
}
